import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import Reveal from "./Reveal";
import { FLEET } from "../data/site";

function FleetCard({ item, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      data-testid={`fleet-card-${item.id}`}
      className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center py-16 md:py-24 border-t border-black/10"
    >
      <div className={`md:col-span-7 ${isEven ? "" : "md:order-2"}`}>
        <div className="clip-frame relative aspect-[16/11] overflow-hidden bg-[color:var(--bs-sand)]">
          <motion.img
            src={item.image}
            alt={item.name}
            style={{ scale, y }}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
          <div className="absolute top-4 left-4 overline bg-white/80 backdrop-blur px-3 py-2">
            {String(index + 1).padStart(2, "0")} / 03
          </div>
        </div>
      </div>

      <div className={`md:col-span-5 ${isEven ? "" : "md:order-1"}`}>
        <Reveal>
          <div className="overline text-[color:var(--bs-muted)]">
            {item.subtitle}
          </div>
          <h3 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[color:var(--bs-ink)]">
            {item.name}
          </h3>
          <p className="mt-5 text-[color:var(--bs-muted)] leading-relaxed font-light max-w-md">
            {item.description}
          </p>
          <dl className="mt-8 space-y-3">
            <div className="flex justify-between border-b border-black/10 py-2">
              <dt className="overline text-[color:var(--bs-muted)]">Capacité</dt>
              <dd className="text-sm text-[color:var(--bs-ink)]">{item.capacity}</dd>
            </div>
            <div className="flex justify-between border-b border-black/10 py-2">
              <dt className="overline text-[color:var(--bs-muted)]">Poids</dt>
              <dd className="text-sm text-[color:var(--bs-ink)] text-right max-w-[60%]">
                {item.weight}
              </dd>
            </div>
            <div className="flex justify-between border-b border-black/10 py-2">
              <dt className="overline text-[color:var(--bs-muted)]">Base</dt>
              <dd className="text-sm text-[color:var(--bs-ink)]">{item.base}</dd>
            </div>
          </dl>
          <div className="mt-8">
            <Link
              to="/flotte"
              data-testid={`fleet-link-${item.id}`}
              className="overline link-underline"
            >
              Voir la fiche →
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

export default function FleetSection() {
  return (
    <section
      id="flotte"
      data-testid="fleet-section"
      className="relative py-24 md:py-32 bg-[color:var(--bs-bg)]"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <Reveal className="md:col-span-8">
            <div className="overline text-[color:var(--bs-muted)]">Flotte</div>
            <h2 className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.95] text-[color:var(--bs-ink)]">
              Trois appareils.
              <br />
              <span className="italic text-[color:var(--bs-gold)]">Trois façons</span> de voler.
            </h2>
          </Reveal>
          <Reveal className="md:col-span-4" delay={0.1}>
            <p className="text-[color:var(--bs-muted)] font-light leading-relaxed">
              Du biplace-école au turbine six-places, chaque appareil de la
              flotte BSCOPTER répond à une intention précise.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 md:mt-24">
          {FLEET.map((f, i) => (
            <FleetCard key={f.id} item={f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
